import React, { useState, useEffect } from 'react';
import useNotificationStore from '../stores/notificationStore';

const NotificationSystem = ({ notifications = [], onDismiss }) => {
  const [visibleNotifications, setVisibleNotifications] = useState([]);

  useEffect(() => {
    setVisibleNotifications(notifications);
  }, [notifications]);

  const handleDismiss = id => {
    setVisibleNotifications(prev => prev.filter(notif => notif.id !== id));
    if (onDismiss) {
      onDismiss(id);
    }
  };

  const getNotificationStyles = type => {
    switch (type) {
      case 'success':
        return 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200';
      case 'error':
        return 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200';
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200';
      case 'info':
      default:
        return 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200';
    }
  };

  const getNotificationIcon = type => {
    switch (type) {
      case 'success':
        return (
          <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
        );
    }
  };

  if (visibleNotifications.length === 0) return null;

  // z-[60]: above WorkspaceDialog and Modal (z-50, and portalled after this tree, so equal
  // z-index would paint them over a toast), below ConfirmModal (z-[9999]). Every toast fill is
  // opaque (dark mode included): a toast landing over the dialog header would otherwise show the
  // Close X / header actions through its text.
  //
  // While a WorkspaceDialog is mounted the stack starts below its header (top-24, past the
  // header at every width) so a toast never covers the dialog's Close X: the X is the only
  // pointer way out of a dialog that ignores its backdrop. `:has()` reads the dialog straight
  // from the DOM, so this needs no wiring between the two and no dialog-side state.
  return (
    <div className="fixed top-4 right-4 z-[60] space-y-3 max-w-[min(24rem,calc(100vw-2rem))] [body:has([data-workspace-dialog])_&]:top-24">
      {visibleNotifications.map(notification => (
        <div
          key={notification.id}
          className={`
            border rounded-lg p-4 shadow-lg transform transition-all duration-300 ease-in-out
            ${getNotificationStyles(notification.type)}
          `}
        >
          <div className="flex items-start">
            <div className="flex-shrink-0">{getNotificationIcon(notification.type)}</div>
            <div className="ml-3 min-w-0 flex-1">
              {notification.title && (
                <h4 className="font-medium mb-1 [overflow-wrap:anywhere]">{notification.title}</h4>
              )}
              <p className="text-sm [overflow-wrap:anywhere]">{notification.message}</p>
            </div>
            <div className="ml-4 flex-shrink-0">
              <button
                onClick={() => handleDismiss(notification.id)}
                className="inline-flex rounded-md p-1.5 hover:bg-black/10 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                aria-label="Dismiss notification"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Hook for managing notifications.
//
// Backed by a single shared Zustand store (`useNotificationStore`) so a toast
// raised anywhere — a component, a custom hook, or a background poller calling
// `useNotificationStore.getState().addNotification(...)` — lands in the same
// queue the root-mounted panel renders. The returned API (and every call
// signature: `addNotification('success', msg)`, the reversed `(msg, 'error')`
// form, and the `({ type, message })` object form) is unchanged, so existing
// call sites need no edits. Fields are selected individually (never as a fresh
// object) to stay compatible with Zustand v5's snapshot caching.
export const useNotifications = () => {
  const notifications = useNotificationStore(state => state.notifications);
  const addNotification = useNotificationStore(state => state.addNotification);
  const removeNotification = useNotificationStore(state => state.removeNotification);
  const showSuccess = useNotificationStore(state => state.showSuccess);
  const showError = useNotificationStore(state => state.showError);
  const showWarning = useNotificationStore(state => state.showWarning);
  const showInfo = useNotificationStore(state => state.showInfo);

  return {
    notifications,
    addNotification,
    removeNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};

export default NotificationSystem;
